import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { affiliate, billing, ApiError } from '@repletoryreach/sdk-api';

/**
 * The refer-and-earn surface, W47.
 *
 * `useAffiliate` answers for an account that has not enrolled as well as one
 * that has: the endpoint says 404 `NOT_ENROLLED`, which is the normal state
 * for nearly every account and not an error, so it is turned into `null` here
 * rather than left for four pages to each unwrap. React Query would otherwise
 * retry a 404 three times before showing the enrolment form.
 */
const AFFILIATE = ['affiliate', 'me'] as const;

function notFound(e: unknown): boolean {
  return e instanceof ApiError && e.status === 404;
}

export function useAffiliateProgramme() {
  return useQuery({
    queryKey: ['affiliate', 'programme'],
    queryFn: () => affiliate.getProgramme(),
    // Terms and a rate change rarely; a partner reading their page twice in a
    // minute should not cost two calls.
    staleTime: 5 * 60_000
  });
}

export function useAffiliate() {
  return useQuery({
    queryKey: AFFILIATE,
    queryFn: async () => {
      try {
        return (await affiliate.me()).data;
      } catch (e) {
        if (notFound(e)) return null;
        throw e;
      }
    },
    retry: (count, e) => !notFound(e) && count < 2,
    staleTime: 15_000
  });
}

export function useAffiliateReferrals(status?: string) {
  return useQuery({
    queryKey: ['affiliate', 'referrals', status ?? 'all'],
    queryFn: () => affiliate.listReferrals({ limit: 200, status }),
    staleTime: 30_000
  });
}

export function useAffiliateEarnings(kind?: string) {
  return useQuery({
    queryKey: ['affiliate', 'earnings', kind ?? 'all'],
    queryFn: () => affiliate.listEarnings({ limit: 200, kind }),
    staleTime: 30_000
  });
}

export function useAffiliateWithdrawals() {
  return useQuery({
    queryKey: ['affiliate', 'withdrawals'],
    queryFn: () => affiliate.listWithdrawals(),
    staleTime: 15_000
  });
}

export function usePayoutAccount() {
  return useQuery({
    queryKey: ['affiliate', 'payout-account'],
    queryFn: async () => {
      try {
        return (await affiliate.getPayoutAccount()).data;
      } catch (e) {
        if (notFound(e)) return null;
        throw e;
      }
    },
    retry: (count, e) => !notFound(e) && count < 2
  });
}

/** Everything a write touches: the record, the balances behind it, and the lists. */
function useInvalidateAffiliate() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['affiliate'] });
    // A conversion to credits moves the wallet the shell is showing.
    void qc.invalidateQueries({ queryKey: ['wallet'] });
  };
}

export function useEnrol() {
  const invalidate = useInvalidateAffiliate();
  return useMutation({
    mutationFn: (body: Parameters<typeof affiliate.enrol>[0]) => affiliate.enrol(body),
    onSuccess: invalidate
  });
}

export function useUpdateAffiliate() {
  const invalidate = useInvalidateAffiliate();
  return useMutation({
    mutationFn: (body: Parameters<typeof affiliate.update>[0]) => affiliate.update(body),
    onSuccess: invalidate
  });
}

export function useRequestWithdrawal() {
  const invalidate = useInvalidateAffiliate();
  return useMutation({
    mutationFn: (body: Parameters<typeof affiliate.requestWithdrawal>[0]) => affiliate.requestWithdrawal(body),
    onSuccess: invalidate
  });
}

export function useCancelWithdrawal() {
  const invalidate = useInvalidateAffiliate();
  return useMutation({
    mutationFn: (id: string) => affiliate.cancelWithdrawal(id),
    onSuccess: invalidate
  });
}

export function useSavePayoutAccount() {
  const invalidate = useInvalidateAffiliate();
  return useMutation({
    mutationFn: (body: Parameters<typeof affiliate.savePayoutAccount>[0]) => affiliate.savePayoutAccount(body),
    onSuccess: invalidate
  });
}

// ── The other side: a company that was referred, or still could be ──────────

export function useReferredBy() {
  return useQuery({
    queryKey: ['billing', 'referral-code'],
    queryFn: () => billing.getReferral(),
    staleTime: 5 * 60_000
  });
}

export function useApplyReferralCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => billing.applyReferralCode(code),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['billing', 'referral-code'] }); }
  });
}
